import init from '../out/client_web';
import version, { byteLength } from 'aether:engine-version';

type Progress = (loaded: number, total: number | null) => void;
let bytes: ArrayBuffer;
let module: WebAssembly.Module;
let ready: ReturnType<typeof init> | undefined;

async function hash(bytes: ArrayBuffer) {
    return Array.from(new Uint8Array(await crypto.subtle.digest('SHA-256', bytes)), n => n.toString(16).padStart(2, '0')).join('');
}

async function load(url: URL, progress?: Progress) {
    url.searchParams.set('ref', version);
    let cache: Cache | undefined;
    try {
        cache = await caches.open('aether-engine-v1');
        const cached = await cache.match(url.href);
        if (cached) {
            const bytes = await cached.arrayBuffer();
            if (await hash(bytes) === version) return bytes;
            await cache.delete(url.href);
        }
    } catch (error) {
        console.debug('Engine cache unavailable', error);
    }
    progress?.(0, byteLength);
    const bytes = await new Promise<ArrayBuffer>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('GET', url.href);
        xhr.responseType = 'arraybuffer';
        xhr.onprogress = e => progress?.(e.loaded, e.lengthComputable ? e.total : byteLength);
        xhr.onerror = () => reject(new Error('Network error fetching engine'));
        xhr.onabort = () => reject(new Error('Engine download aborted'));
        xhr.onload = () => xhr.status >= 200 && xhr.status < 300
            ? resolve(xhr.response) : reject(new Error(`Failed to load engine: ${xhr.status}`));
        xhr.send();
    });
    if (await hash(bytes) !== version) throw new Error('Engine changed during loading. Reload to get the latest build.');
    try {
        await cache?.put(url.href, new Response(bytes, { headers: { 'Content-Type': 'application/wasm' } }));
    } catch (error) {
        console.debug('Engine cache write unavailable', error);
    }
    return bytes;
}

export function initEngine(url = new URL('client_web_bg.wasm', document.baseURI), progress?: Progress, initializing?: () => void) {
    return ready ??= (async () => {
        bytes = await load(url, progress);
        initializing?.();
        module = await WebAssembly.compile(bytes);
        return init({ module_or_path: module });
    })().catch(error => { ready = undefined; throw error; });
}

export function engineModule() {
    if (!module) throw new Error('Engine is not initialized');
    return module;
}

// The opaque-origin script sandbox cannot receive a compiled WebAssembly.Module.
export function engineBytes() {
    if (!bytes) throw new Error('Engine is not initialized');
    return bytes;
}
